import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowLeft,
  Briefcase,
  Plus,
  Copy,
  CheckCircle2,
  XCircle,
  Clock,
  Building2,
  Mail,
  ExternalLink,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../App';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TIER_PRESETS = [
  { id: 'insights', label: 'Insights', price: 199, cadence: 'monthly', max_recipients: 3 },
  { id: 'performance', label: 'Performance', price: 499, cadence: 'monthly', max_recipients: 5 },
  { id: 'enterprise', label: 'Enterprise', price: 999, cadence: 'monthly', max_recipients: 15 },
];

const STATUS_STYLE = {
  pending: { cls: 'bg-amber-100 text-amber-800 border-amber-300', icon: Clock, label: 'Awaiting acceptance' },
  accepted: { cls: 'bg-blue-100 text-blue-800 border-blue-300', icon: Clock, label: 'Awaiting payment' },
  active: { cls: 'bg-emerald-100 text-emerald-800 border-emerald-300', icon: CheckCircle2, label: 'Active' },
  expired: { cls: 'bg-slate-100 text-slate-700 border-slate-300', icon: XCircle, label: 'Expired' },
  canceled: { cls: 'bg-rose-100 text-rose-800 border-rose-300', icon: XCircle, label: 'Canceled' },
};

const AdminAnalyticsOffersPage = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [tours, setTours] = useState([]);
  const [offers, setOffers] = useState([]);
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [hideExpired, setHideExpired] = useState(true);

  const [form, setForm] = useState({
    org_name: '',
    contact_name: '',
    contact_email: '',
    tour_id: '',
    tier: 'insights',
    price_usd: 199,
    billing_interval: 'monthly',
    report_cadence: 'monthly',
    max_recipients: 3,
    free_trial_days: 0,
    setup_fee_usd: 0,
    discount_pct: 0,
    notes: '',
    co_branded: true,
    white_label: false,
  });

  const fetchAll = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [t, o, s] = await Promise.all([
        axios.get(`${API}/admin/tours`, { headers }),
        axios.get(`${API}/admin/analytics-offers`, { headers }),
        axios.get(`${API}/admin/analytics-subscriptions`, { headers }),
      ]);
      setTours(t.data || []);
      setOffers(o.data || []);
      setSubs(s.data || []);
      if (!form.tour_id && t.data?.length) {
        setForm((f) => ({ ...f, tour_id: t.data[0].tour_id }));
      }
    } catch (e) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const applyTierPreset = (tierId) => {
    const t = TIER_PRESETS.find((x) => x.id === tierId);
    if (!t) return;
    setForm((f) => ({
      ...f,
      tier: t.id,
      price_usd: t.price,
      report_cadence: t.cadence,
      max_recipients: t.max_recipients,
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.org_name || !form.contact_email || !form.tour_id) {
      toast.error('Org, email and tour are required');
      return;
    }
    setCreating(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/analytics-offers`,
        { ...form, price_usd: Number(form.price_usd) },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Offer created — copy the magic link to send');
      setOffers([data, ...offers]);
      setShowForm(false);
      setForm((f) => ({ ...f, org_name: '', contact_name: '', contact_email: '', notes: '' }));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create offer');
    } finally {
      setCreating(false);
    }
  };

  const copyLink = (token2) => {
    const url = `${window.location.origin}/analytics-offer/${token2}`;
    navigator.clipboard.writeText(url);
    toast.success('Magic link copied');
  };

  const expireOffer = async (offer_id) => {
    if (!window.confirm('Expire this offer? The magic link will stop working.')) return;
    try {
      await axios.put(
        `${API}/admin/analytics-offers/${offer_id}/expire`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Offer expired');
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
  };

  const cancelSub = async (sub_id) => {
    if (!window.confirm('Cancel this subscription at period end? Reports continue until period end.')) return;
    try {
      await axios.put(
        `${API}/admin/analytics-subscriptions/${sub_id}/cancel`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Subscription scheduled to cancel at period end');
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-violet-500 border-t-transparent"></div>
      </div>
    );
  }

  const activeCount = subs.filter((s) => s.status === 'active').length;
  const mrr = subs
    .filter((s) => s.status === 'active' && s.billing_interval === 'monthly')
    .reduce((sum, s) => sum + Number(s.price_usd || 0), 0)
    + subs
      .filter((s) => s.status === 'active' && s.billing_interval === 'yearly')
      .reduce((sum, s) => sum + Number(s.price_usd || 0) / 12, 0);

  return (
    <div className="min-h-screen bg-slate-50 pb-24" data-testid="admin-analytics-offers-page">
      <header className="bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white py-6 px-4">
        <div className="max-w-6xl mx-auto">
          <button
            onClick={() => navigate('/admin')}
            className="flex items-center gap-2 text-white/80 hover:text-white mb-4 transition-colors"
            data-testid="back-to-admin-btn"
          >
            <ArrowLeft className="w-5 h-5" />
            Back to Dashboard
          </button>
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <h1 className="text-3xl font-black uppercase flex items-center gap-3">
                <Briefcase className="w-8 h-8" /> B2B Analytics Subscriptions
              </h1>
              <p className="text-violet-100 mt-1">
                Send a magic-link offer · client accepts on a branded page · Stripe charges them on a recurring subscription.
              </p>
            </div>
            <button
              onClick={() => setShowForm((v) => !v)}
              data-testid="toggle-create-offer-form"
              className="bg-white text-violet-700 px-5 py-3 rounded-full font-bold shadow-lg hover:scale-105 transition flex items-center gap-2"
            >
              <Plus className="w-5 h-5" />
              {showForm ? 'Hide form' : 'New offer'}
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8 space-y-8">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Stat label="Active subscriptions" value={activeCount} />
          <Stat label="Monthly recurring" value={`$${mrr.toFixed(0)}`} />
          <Stat label="Outstanding offers" value={offers.filter((o) => o.status === 'pending' || o.status === 'accepted').length} />
        </div>

        {showForm && (
          <form
            onSubmit={submit}
            data-testid="offer-form"
            className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm space-y-4"
          >
            <h2 className="text-xl font-black text-slate-900">Create new offer</h2>

            <div className="flex flex-wrap gap-2">
              {TIER_PRESETS.map((t) => (
                <button
                  type="button"
                  key={t.id}
                  onClick={() => applyTierPreset(t.id)}
                  data-testid={`tier-preset-${t.id}`}
                  className={`px-4 py-2 rounded-full text-sm font-bold transition ${
                    form.tier === t.id
                      ? 'bg-violet-600 text-white'
                      : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                  }`}
                >
                  {t.label} · ${t.price}/mo
                </button>
              ))}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Organization name" required>
                <input
                  type="text"
                  data-testid="org-name-input"
                  value={form.org_name}
                  onChange={(e) => setForm({ ...form, org_name: e.target.value })}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2"
                  placeholder="Tel Aviv Museum of Art"
                  required
                />
              </Field>
              <Field label="Tour" required>
                <select
                  data-testid="tour-select"
                  value={form.tour_id}
                  onChange={(e) => setForm({ ...form, tour_id: e.target.value })}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 bg-white"
                  required
                >
                  <option value="">— select tour —</option>
                  {tours.map((t) => (
                    <option key={t.tour_id} value={t.tour_id}>
                      {t.title} ({t.city})
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Contact name" required>
                <input
                  type="text"
                  data-testid="contact-name-input"
                  value={form.contact_name}
                  onChange={(e) => setForm({ ...form, contact_name: e.target.value })}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2"
                  required
                />
              </Field>
              <Field label="Contact email" required>
                <input
                  type="email"
                  data-testid="contact-email-input"
                  value={form.contact_email}
                  onChange={(e) => setForm({ ...form, contact_email: e.target.value })}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2"
                  required
                />
              </Field>
              <Field label="Price (USD)" required>
                <input
                  type="number"
                  min="1"
                  step="1"
                  data-testid="price-input"
                  value={form.price_usd}
                  onChange={(e) => setForm({ ...form, price_usd: e.target.value })}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2"
                  required
                />
              </Field>
              <Field label="Billing interval">
                <select
                  data-testid="billing-interval-select"
                  value={form.billing_interval}
                  onChange={(e) => setForm({ ...form, billing_interval: e.target.value })}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 bg-white"
                >
                  <option value="monthly">Monthly</option>
                  <option value="yearly">Yearly (10% off built-in)</option>
                </select>
              </Field>
              <Field label="Report cadence">
                <select
                  data-testid="cadence-select"
                  value={form.report_cadence}
                  onChange={(e) => setForm({ ...form, report_cadence: e.target.value })}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 bg-white"
                >
                  <option value="weekly">Weekly</option>
                  <option value="monthly">Monthly</option>
                  <option value="quarterly">Quarterly</option>
                  <option value="annual">Annual</option>
                </select>
              </Field>
              <Field label="Max recipients">
                <input
                  type="number"
                  min="1"
                  data-testid="max-recipients-input"
                  value={form.max_recipients}
                  onChange={(e) => setForm({ ...form, max_recipients: Number(e.target.value) })}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2"
                />
              </Field>
            </div>

            <Field label="Internal notes (not shown to client)">
              <textarea
                data-testid="notes-input"
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                className="w-full border border-slate-300 rounded-lg px-3 py-2"
                rows={2}
              />
            </Field>

            <div className="flex flex-wrap gap-4">
              <label className="flex items-center gap-2 text-sm text-slate-700">
                <input
                  type="checkbox"
                  data-testid="cobranded-checkbox"
                  checked={form.co_branded}
                  onChange={(e) => setForm({ ...form, co_branded: e.target.checked })}
                />
                Co-branded report
              </label>
              <label className="flex items-center gap-2 text-sm text-slate-700">
                <input
                  type="checkbox"
                  data-testid="whitelabel-checkbox"
                  checked={form.white_label}
                  onChange={(e) => setForm({ ...form, white_label: e.target.checked })}
                />
                White-label
              </label>
            </div>

            <div className="flex justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={() => setShowForm(false)}
                className="px-5 py-2 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={creating}
                data-testid="create-offer-submit"
                className="px-6 py-2 rounded-full bg-gradient-to-r from-violet-600 to-fuchsia-600 hover:opacity-90 text-white font-bold disabled:opacity-50"
              >
                {creating ? 'Creating…' : 'Create offer & magic link'}
              </button>
            </div>
          </form>
        )}

        <Section title="Offers">
          <div className="flex justify-end mb-2">
            <label className="inline-flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
              <input
                type="checkbox"
                data-testid="hide-expired-toggle"
                checked={hideExpired}
                onChange={(e) => setHideExpired(e.target.checked)}
                className="w-4 h-4"
              />
              Hide expired & canceled
            </label>
          </div>
          {(() => {
            const visible = hideExpired
              ? offers.filter((o) => o.status !== 'expired' && o.status !== 'canceled')
              : offers;
            if (visible.length === 0) {
              return (
                <Empty
                  text={
                    hideExpired && offers.length > 0
                      ? 'No active offers — uncheck "Hide expired" to see archived ones.'
                      : 'No offers yet. Create one to start charging partners on recurring billing.'
                  }
                />
              );
            }
            return (
              <div className="space-y-3">
                {visible.map((o) => {
                const meta = STATUS_STYLE[o.status] || STATUS_STYLE.pending;
                const Icon = meta.icon;
                return (
                  <div
                    key={o.offer_id}
                    data-testid={`offer-row-${o.offer_id}`}
                    className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm"
                  >
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      <div>
                        <h3 className="text-lg font-black text-slate-900 flex items-center gap-2">
                          <Building2 className="w-5 h-5 text-violet-600" />
                          {o.org_name}
                        </h3>
                        <p className="text-sm text-slate-600 mt-1">
                          <Mail className="w-4 h-4 inline mr-1" />
                          {o.contact_email} · Tour: {o.tour_title}
                        </p>
                        <p className="text-sm text-slate-700 mt-2">
                          <strong className="capitalize">{o.tier}</strong> · ${o.final_price_usd}/{o.billing_interval === 'yearly' ? 'yr' : 'mo'} ·{' '}
                          {o.report_cadence} reports · up to {o.max_recipients} recipients
                        </p>
                      </div>
                      <span className={`px-3 py-1 rounded-full text-xs font-bold uppercase border inline-flex items-center gap-1.5 ${meta.cls}`}>
                        <Icon className="w-3.5 h-3.5" />
                        {meta.label}
                      </span>
                    </div>
                    <div className="mt-3 flex flex-wrap gap-2">
                      {(o.status === 'pending' || o.status === 'accepted') && (
                        <>
                          <button
                            onClick={() => copyLink(o.invite_token)}
                            data-testid={`copy-link-${o.offer_id}`}
                            className="text-xs font-bold uppercase px-3 py-1.5 rounded-full bg-violet-100 text-violet-700 hover:bg-violet-200 inline-flex items-center gap-1"
                          >
                            <Copy className="w-3.5 h-3.5" /> Copy magic link
                          </button>
                          <a
                            href={`/analytics-offer/${o.invite_token}`}
                            target="_blank"
                            rel="noreferrer"
                            className="text-xs font-bold uppercase px-3 py-1.5 rounded-full bg-slate-100 text-slate-700 hover:bg-slate-200 inline-flex items-center gap-1"
                          >
                            <ExternalLink className="w-3.5 h-3.5" /> Preview
                          </a>
                          <button
                            onClick={() => expireOffer(o.offer_id)}
                            data-testid={`expire-offer-${o.offer_id}`}
                            className="text-xs font-bold uppercase px-3 py-1.5 rounded-full bg-rose-100 text-rose-700 hover:bg-rose-200"
                          >
                            Expire
                          </button>
                        </>
                      )}
                      <span className="text-xs text-slate-500 self-center ml-auto">
                        Created {new Date(o.created_at).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
            );
          })()}
        </Section>

        <Section title="Active subscriptions">
          {subs.length === 0 ? (
            <Empty text="Once a client pays via the magic link, their subscription appears here." />
          ) : (
            <div className="space-y-3">
              {subs.map((s) => (
                <div
                  key={s.subscription_id}
                  data-testid={`sub-row-${s.subscription_id}`}
                  className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm"
                >
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div>
                      <h3 className="text-lg font-black text-slate-900">{s.org_name}</h3>
                      <p className="text-sm text-slate-600 mt-1">{s.contact_email} · Tour: {s.tour_title}</p>
                      <p className="text-sm text-slate-700 mt-2">
                        <strong className="capitalize">{s.tier}</strong> · ${s.price_usd}/{s.billing_interval === 'yearly' ? 'yr' : 'mo'} ·{' '}
                        {s.report_cadence} · renews {s.current_period_end ? new Date(s.current_period_end).toLocaleDateString() : '—'}
                      </p>
                      {s.cancel_at_period_end && (
                        <p className="text-xs font-bold text-rose-700 mt-2">Will end at period end</p>
                      )}
                    </div>
                    <span className="px-3 py-1 rounded-full text-xs font-bold uppercase border bg-emerald-100 text-emerald-800 border-emerald-300">
                      {s.status}
                    </span>
                  </div>
                  {!s.cancel_at_period_end && (
                    <div className="mt-3 flex justify-end">
                      <button
                        onClick={() => cancelSub(s.subscription_id)}
                        data-testid={`cancel-sub-${s.subscription_id}`}
                        className="text-xs font-bold uppercase px-3 py-1.5 rounded-full bg-rose-100 text-rose-700 hover:bg-rose-200"
                      >
                        Cancel at period end
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </Section>
      </main>
    </div>
  );
};

const Stat = ({ label, value }) => (
  <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm">
    <div className="text-xs uppercase tracking-wider text-slate-500 font-bold">{label}</div>
    <div className="text-3xl font-black text-slate-900 mt-1">{value}</div>
  </div>
);

const Field = ({ label, required, children }) => (
  <label className="block">
    <span className="text-sm font-bold text-slate-700">
      {label} {required && <span className="text-rose-500">*</span>}
    </span>
    <div className="mt-1">{children}</div>
  </label>
);

const Section = ({ title, children }) => (
  <section>
    <h2 className="text-xl font-black text-slate-900 mb-3">{title}</h2>
    {children}
  </section>
);

const Empty = ({ text }) => (
  <div className="bg-white rounded-2xl border border-dashed border-slate-300 p-10 text-center text-slate-600">
    {text}
  </div>
);

export default AdminAnalyticsOffersPage;
